// Asking before something that cannot be undone, instead of window.confirm.
//
// Promise: `confirmAction({ title, body, action })` resolves true only when the
// person presses the action. The text names the target and what goes with it
// (e.g. "Delete the folder x. Its favorites move out of it."). One layer,
// mounted by the shell.
import { m } from "@trameria/i18n";
import { type ReactNode, useSyncExternalStore } from "react";
import { Button } from "./button";
import { Modal } from "./modal";

interface Ask {
  title: string;
  body?: string;
  /**
   * Text shown as is (what will be sent, where a link goes), so the person
   * sees it outside whatever asked (a UI inside an iframe can draw anything).
   */
  quote?: string;
  /** Shown under the body as drawn (what the action covers). */
  detail?: ReactNode;
  action: string;
  danger?: boolean;
  resolve(ok: boolean): void;
}

let current: Ask | null = null;
const subs = new Set<() => void>();
const emit = () => {
  for (const fn of subs) fn();
};

export function confirmAction(ask: Omit<Ask, "resolve">): Promise<boolean> {
  current?.resolve(false);
  return new Promise((resolve) => {
    current = { ...ask, resolve };
    emit();
  });
}

const subscribe = (fn: () => void) => {
  subs.add(fn);
  return () => subs.delete(fn);
};
const read = () => current;

export function ConfirmLayer() {
  const ask = useSyncExternalStore(subscribe, read, read);
  const answer = (ok: boolean) => {
    ask?.resolve(ok);
    current = null;
    emit();
  };
  return (
    <Modal
      open={ask !== null}
      onClose={() => answer(false)}
      label={ask?.title ?? ""}
    >
      {ask && (
        <>
          <div className="text-[15px] text-fg">{ask.title}</div>
          {ask.body && (
            <p className="mt-2 text-[13px] leading-[1.7] text-muted">
              {ask.body}
            </p>
          )}
          {/* A long detail scrolls; the buttons stay in view. */}
          {ask.detail && (
            <div className="-mx-1 mt-3 max-h-[55vh] overflow-y-auto px-1">
              {ask.detail}
            </div>
          )}
          {ask.quote !== undefined && (
            <div className="mt-3 max-h-[40vh] select-text overflow-y-auto whitespace-pre-wrap break-words rounded-[9px] bg-hover px-3 py-2 text-[13px] leading-[1.7] text-body">
              {ask.quote}
            </div>
          )}
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="ghost" onClick={() => answer(false)}>
              {m.common_cancel()}
            </Button>
            <Button
              variant={ask.danger ? "danger" : "primary"}
              onClick={() => answer(true)}
            >
              {ask.action}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
